import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faCircleInfo } from '@fortawesome/free-solid-svg-icons';
import { Subject } from 'rxjs';
import { DateReachedTriggerData } from 'src/app/components/designer/models/date-reached-trigger-data';
import { Attribute } from 'src/app/models/attribute';
import { PropertyType } from 'src/app/models/enums/property-type';
import { FormMode } from '../../common/form-mode';
import { ContextualMenuEvent } from '../contextual-menu-event';
import { NeedAttributes } from '../need-attributes';

@Component({
    selector: 'app-workflow-trigger-date-reached-data',
    imports: [FormsModule, ReactiveFormsModule, MatFormFieldModule, MatInputModule, MatSelectModule, MatOptionModule, FaIconComponent, CommonModule, MatTooltipModule],
    templateUrl: './workflow-trigger-date-reached-data.component.html',
    styleUrl: './workflow-trigger-date-reached-data.component.scss',
})
export class WorkflowTriggerDateReachedDataComponent implements OnInit, NeedAttributes {
    /**
     * Liste des icones utilisés dans le template.
     */
    public faCircleInfo = faCircleInfo;

    /**
     * Liste des Enums utilisées dans le template.
     */
    public propertyType: typeof PropertyType = PropertyType;
    public FormMode = FormMode;

    /**
     * Données affichées par le composant dynamique.
     */
    public _data: DateReachedTriggerData;

    /**
     * Spécifie les données affichées par le composant dynamique.
     */
    set data(value: string | DateReachedTriggerData) {
        if (typeof value === 'string') {
            this._data = JSON.parse(value);
        } else {
            this._data = value as DateReachedTriggerData;
        }

        this.fillForm();
    }

    /**
     * Obtient les données affichées par le composant dynamique.
     */
    get data(): DateReachedTriggerData {
        this.fillData();
        return this._data;
    }

    /**
     * Mode d'affichage du formulaire.
     */
    mode: FormMode;

    /**
     * Source du menu contextuel.
     */
    contextualMenuSource: Subject<ContextualMenuEvent> = new Subject<ContextualMenuEvent>();

    /**
     * Liste des attributs de la vue.
     */
    public _attributes: Attribute[];

    /**
     * Spécifie la liste des attributs de la vue.
     */
    set attributes(value: Attribute[]) {
        this._attributes = value
            .filter((x) => (x.type === PropertyType.Date || x.type === PropertyType.DateAndTime) && x.custom === true)
            .sort((a, b) => {
                return a.header < b.header ? -1 : 1;
            });
        this.fillForm();
    }

    /**
     * Instance du formulaire.
     */
    public dataForm: UntypedFormGroup;

    /**
     * Attribut selectionné par l'utilisateur
     */
    public selectedAttribute: Attribute = null;

    /**
     * Constructeur.
     */
    constructor(private changeDetector: ChangeDetectorRef) {}

    /**
     * Appellé lors de l'initialisation du contrôle.
     */
    ngOnInit(): void {
        this.dataForm = new UntypedFormGroup({
            attribute: new UntypedFormControl('', [Validators.required]),
        });

        this.fillForm();
    }

    /**
     * Indique si le formulaire est valide.
     */
    public isValid(): boolean {
        return this.dataForm.valid;
    }

    /**
     * Retourne une valeur indiquant si l'utilisateur a modifié des valeurs du formulaire.
     */
    public get dirty(): boolean {
        return this.dataForm && this.dataForm.dirty;
    }

    /**
     * Annulle les changements effectués par l'utilisateur.
     */
    public clearData() {
        this.dataForm.markAsPristine();
    }

    /**
     * Remplit le formulaire avec les données.
     */
    private fillForm() {
        if (this.dataForm) {
            if (this._data && this._attributes) {
                this.dataForm.get('attribute').setValue(this._data.attributeId);
            }
        }
    }

    /**
     * Met à jour les données à partir du formulaire.
     */
    private fillData() {
        let updatedData: DateReachedTriggerData = new DateReachedTriggerData();
        updatedData.attributeId = this.dataForm.get('attribute').value;

        this._data = updatedData;
    }
}
